import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VersionHistoryDialog, ProposalEditForm, ApproverDialog, SendToClientDialog, ManualApprovalDialog } from '../components/proposals/ProposalDialogs';
import { ArrowLeft, Edit, Send, Check, X, FileText, MessageSquare, History, Clock, Trash2, Upload, Eye } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  'Individual - Residential',
  'Housing',
  'Commercial',
  'Institutional',
  'Hospitality'
];

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'for_review', label: 'For Review' },
  { value: 'sent_to_client', label: 'Sent to Client' },
  { value: 'signed', label: 'Signed' },
  { value: 'rejected', label: 'Rejected' }
];

export const ProposalDetailPage = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isApproverDialog, setIsApproverDialog] = useState(false);
  const [isSendDialog, setIsSendDialog] = useState(false);
  const [isManualApprovalDialog, setIsManualApprovalDialog] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [selectedApprover, setSelectedApprover] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [approverComments, setApproverComments] = useState('');
  const [manualApprovalDate, setManualApprovalDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState('');

  const fetchProposal = useCallback(async () => {
    try {
      const [proposalRes, teamRes, contactsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/proposals/${proposalId}`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true }),
        axios.get(`${API_URL}/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true })
      ]);
      setProposal(proposalRes.data);
      setEditForm(proposalRes.data);
      setTeamMembers(teamRes.data);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  const fetchVersionHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/proposals/${proposalId}/versions`, { withCredentials: true });
      setVersionHistory(res.data.version_history || []);
      setIsVersionHistoryOpen(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    try {
      await axios.post(`${API_URL}/proposals/${proposalId}/restore-version/${versionNumber}`, {}, { withCredentials: true });
      toast.success(`Restored to version ${versionNumber}`);
      setIsVersionHistoryOpen(false);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  useEffect(() => {
    fetchProposal();
  }, [fetchProposal]);

  const handleUpdate = async () => {
    try {
      const res = await axios.patch(`${API_URL}/proposals/${proposalId}`, editForm, { withCredentials: true });
      toast.success(`Proposal updated (v${res.data.version})`);
      setIsEditing(false);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const handleInlineEdit = async (field, value) => {
    try {
      const res = await axios.patch(`${API_URL}/proposals/${proposalId}`, { [field]: value }, { withCredentials: true });
      toast.success('Updated');
      setEditingField(null);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const startInlineEdit = (field, currentValue) => {
    setEditingField(field);
    setFieldValue(currentValue || '');
  };

  const handleSendForInternalApproval = async () => {
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/send-for-internal-approval`,
        null,
        { params: { approver_id: selectedApprover }, withCredentials: true }
      );
      toast.success('Sent for internal approval');
      setIsApproverDialog(false);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to send for approval');
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/approve-internal`,
        null,
        { params: { comments: approverComments }, withCredentials: true }
      );
      toast.success('Proposal approved and marked as Sent to Client');
      fetchProposal();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReturn = async () => {
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/return-to-sender`,
        null,
        { params: { comments: approverComments }, withCredentials: true }
      );
      toast.success('Proposal returned to sender');
      fetchProposal();
    } catch (error) {
      toast.error('Failed to return proposal');
    }
  };

  const handleSendToClient = async (useZohoSign) => {
    try {
      if (useZohoSign) {
        await axios.post(
          `${API_URL}/proposals/${proposalId}/send-for-signature`,
          null,
          { params: { recipient_email: recipientEmail }, withCredentials: true }
        );
        toast.success('Sent to client via Zoho Sign');
      } else {
        // Manual approval flow
        setIsManualApprovalDialog(true);
        return;
      }
      setIsSendDialog(false);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to send to client');
    }
  };

  const handleConfirm = async () => {
    try {
      await axios.post(`${API_URL}/proposals/${proposalId}/confirm`, {}, { withCredentials: true });
      toast.success('Proposal confirmed');
      fetchProposal();
    } catch (error) {
      toast.error('Failed to confirm proposal');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API_URL}/proposals/${proposalId}`, { status: newStatus }, { withCredentials: true });
      const labels = { draft: 'Draft', for_review: 'For Review', sent_to_client: 'Sent to Client', signed: 'Signed', rejected: 'Rejected' };
      toast.success(`Proposal marked as ${labels[newStatus] || newStatus}`);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/upload-document`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded');
      fetchProposal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleDeleteDocument = async (idx) => {
    try {
      await axios.delete(`${API_URL}/proposals/${proposalId}/documents/${idx}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchProposal();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleManualApproval = async () => {
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/manual-approval`,
        null,
        { params: { approval_date: manualApprovalDate }, withCredentials: true }
      );
      toast.success('Manual approval recorded');
      setIsManualApprovalDialog(false);
      setIsSendDialog(false);
      fetchProposal();
    } catch (error) {
      toast.error('Failed to record approval');
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this proposal?')) return;
    try {
      await axios.post(
        `${API_URL}/proposals/${proposalId}/reject`,
        null,
        { params: { reason: approverComments }, withCredentials: true }
      );
      toast.success('Proposal rejected');
      fetchProposal();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleConvert = async () => {
    try {
      await axios.post(`${API_URL}/proposals/${proposalId}/convert`, {}, { withCredentials: true });
      toast.success('Converted to project');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to convert');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/proposals/${proposalId}`, { withCredentials: true });
      toast.success('Proposal deleted');
      navigate('/proposals');
    } catch (error) {
      toast.error('Failed to delete proposal');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!proposal) {
    return <Card className="p-12 text-center"><p className="text-muted-foreground">Proposal not found</p></Card>;
  }

  const isCreator = proposal.created_by === user?.user_id;
  const isApprover = proposal.approver_id === user?.user_id;
  const canEdit = true; // Allow editing for all authenticated users
  const isAdmin = user?.role === 'admin';
  const canApprove = isApprover && proposal.status === 'for_review';

  const renderInlineEditField = (field, value, multiline = false) => {
    if (editingField === field) {
      return (
        <div className="flex gap-2 items-start">
          {multiline ? (
            <Textarea
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              className="flex-1"
              rows={3}
              autoFocus
            />
          ) : (
            <Input
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              className="flex-1"
              autoFocus
            />
          )}
          <Button size="sm" onClick={() => handleInlineEdit(field, fieldValue)}><Check className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
        </div>
      );
    }
    return (
      <div 
        className={`group ${canEdit ? 'cursor-pointer hover:bg-muted/50' : ''} p-2 -m-2 rounded`}
        onClick={() => canEdit && startInlineEdit(field, value)}
      >
        <p className="text-sm">{value || <span className="text-muted-foreground italic">Click to add</span>}</p>
        {canEdit && <Edit className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 inline ml-2" />}
      </div>
    );
  };

  return (
    <div data-testid="proposal-detail-page">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/proposals')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Proposals
        </Button>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {editingField === 'title' ? (
                <div className="flex gap-2 items-center">
                  <Input
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    className="text-2xl font-bold w-80"
                    autoFocus
                  />
                  <Button size="sm" onClick={() => handleInlineEdit('title', fieldValue)}><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <h1 
                  className="text-4xl font-heading font-bold tracking-tight cursor-pointer hover:bg-muted/50 rounded px-2 -mx-2"
                  onClick={() => startInlineEdit('title', proposal.title)}
                >
                  {proposal.title}
                  <Edit className="h-4 w-4 text-muted-foreground inline ml-2 opacity-50" />
                </h1>
              )}
              <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">v{proposal.version || 1}</span>
            </div>
            {editingField === 'client_name' ? (
              <div className="flex gap-2 items-center">
                <Select value={fieldValue} onValueChange={(value) => setFieldValue(value)}>
                  <SelectTrigger className="w-60">
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.client_id} value={c.name}>
                        {c.name} {c.company_name ? `(${c.company_name})` : '(Individual)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => handleInlineEdit('client_name', fieldValue)}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <p 
                className="text-lg text-muted-foreground cursor-pointer hover:bg-muted/50 rounded px-2 -mx-2 inline-block"
                onClick={() => startInlineEdit('client_name', proposal.client_name)}
              >
                {proposal.client_name}
                <Edit className="h-3 w-3 text-muted-foreground inline ml-2 opacity-50" />
              </p>
            )}
          </div>
          <div className="flex gap-2 items-start">
            <Button variant="outline" size="sm" onClick={fetchVersionHistory}>
              <History className="mr-2 h-4 w-4" />
              History
            </Button>
            {editingField === 'status' ? (
              <div className="flex gap-2 items-center">
                <Select value={fieldValue} onValueChange={(value) => setFieldValue(value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => handleInlineEdit('status', fieldValue)}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
              </div>
            ) : (
              <span 
                className="px-3 py-1 text-sm font-semibold rounded-full bg-chart-2/20 text-chart-2 cursor-pointer hover:opacity-80"
                onClick={() => startInlineEdit('status', proposal.status)}
              >
                {proposal.status.replace('_', ' ').toUpperCase()}
                <Edit className="h-3 w-3 inline ml-1 opacity-50" />
              </span>
            )}
            {isAdmin && editingField === 'category' ? (
              <div className="flex gap-2 items-center">
                <Select value={fieldValue} onValueChange={(value) => setFieldValue(value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => handleInlineEdit('category', fieldValue)}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
              </div>
            ) : proposal.category ? (
              <span 
                className={`px-3 py-1 text-sm font-semibold rounded-full bg-muted ${isAdmin ? 'cursor-pointer hover:opacity-80' : ''}`}
                onClick={() => isAdmin && startInlineEdit('category', proposal.category)}
              >
                {proposal.category}
                {isAdmin && <Edit className="h-3 w-3 inline ml-1 opacity-50" />}
              </span>
            ) : isAdmin ? (
              <span 
                className="px-3 py-1 text-sm font-semibold rounded-full bg-muted/50 text-muted-foreground cursor-pointer hover:opacity-80"
                onClick={() => startInlineEdit('category', '')}
              >
                + Add Category
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={isVersionHistoryOpen} onOpenChange={setIsVersionHistoryOpen}
        proposal={proposal} versionHistory={versionHistory} onRestore={handleRestoreVersion}
      />

      {isEditing ? (
        <ProposalEditForm
          editForm={editForm} setEditForm={setEditForm}
          onSave={handleUpdate} onCancel={() => setIsEditing(false)}
          categories={categories} companies={companies} contacts={contacts}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <Card className="lg:col-span-2 p-6 space-y-6">
              <div>
                <h3 className="text-lg font-heading font-semibold mb-2">Requirement</h3>
                {renderInlineEditField("requirement", proposal.requirement, true)}
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold mb-2">Scope/Area</h3>
                {renderInlineEditField("scope_area", proposal.scope_area, true)}
              </div>
              {(proposal.final_proposal || canEdit) && (
                <div>
                  <h3 className="text-lg font-heading font-semibold mb-2">Final Proposal</h3>
                  {renderInlineEditField("final_proposal", proposal.final_proposal, true)}
                </div>
              )}
              <div>
                <h3 className="text-lg font-heading font-semibold mb-2">Description</h3>
                {renderInlineEditField("description", proposal.description, true)}
              </div>
            </Card>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-heading font-semibold mb-4">Details</h3>

                {/* Company - inline edit */}
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">Company</p>
                  {editingField === 'company_id' ? (
                    <div className="flex gap-2 items-center mt-1">
                      <Select value={fieldValue || '__none__'} onValueChange={(value) => setFieldValue(value === '__none__' ? '' : value)}>
                        <SelectTrigger className="w-52">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- No Company --</SelectItem>
                          {companies.map(c => (
                            <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => {
                        const company = companies.find(c => c.company_id === fieldValue);
                        handleInlineEdit('company_id', fieldValue);
                        if (company) {
                          // Also update company_name
                          axios.patch(`${API_URL}/proposals/${proposalId}`, { company_name: company.name }, { withCredentials: true });
                        }
                      }}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <p 
                      className={`text-sm font-medium mt-1 ${canEdit ? 'cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 py-0.5' : ''}`}
                      onClick={() => canEdit && startInlineEdit('company_id', proposal.company_id || '')}
                      data-testid="proposal-company-field"
                    >
                      {proposal.company_id ? (companies.find(c => c.company_id === proposal.company_id)?.name || proposal.company_name || 'Unknown') : <span className="text-muted-foreground italic">Click to add</span>}
                      {canEdit && <Edit className="h-3 w-3 text-muted-foreground inline ml-2 opacity-50" />}
                    </p>
                  )}
                </div>

                {/* Contact - inline edit */}
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">Contact</p>
                  {editingField === 'client_name' ? (
                    <div className="flex gap-2 items-center mt-1">
                      <Select value={fieldValue} onValueChange={(value) => setFieldValue(value)}>
                        <SelectTrigger className="w-52">
                          <SelectValue placeholder="Select contact" />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.map(c => (
                            <SelectItem key={c.client_id} value={c.name}>
                              {c.name} {c.company_name ? `(${c.company_name})` : '(Individual)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => handleInlineEdit('client_name', fieldValue)}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <p 
                      className={`text-sm font-medium mt-1 ${canEdit ? 'cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 py-0.5' : ''}`}
                      onClick={() => canEdit && startInlineEdit('client_name', proposal.client_name || '')}
                      data-testid="proposal-contact-field"
                    >
                      {proposal.client_name || <span className="text-muted-foreground italic">Click to add</span>}
                      {canEdit && <Edit className="h-3 w-3 text-muted-foreground inline ml-2 opacity-50" />}
                    </p>
                  )}
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">Amount</p>
                  {editingField === 'amount' ? (
                    <div className="flex gap-2 items-center mt-1">
                      <Input
                        type="number"
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        className="w-32"
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleInlineEdit('amount', parseFloat(fieldValue))}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}><X className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <p 
                      className={`text-2xl font-heading font-bold ${canEdit ? 'cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1' : ''}`}
                      onClick={() => canEdit && startInlineEdit('amount', proposal.amount || '')}
                    >
                      {proposal.amount ? `INR ${proposal.amount.toLocaleString('en-IN')}` : <span className="text-muted-foreground text-base">Click to add</span>}
                      {canEdit && <Edit className="h-3 w-3 text-muted-foreground inline ml-2 opacity-50" />}
                    </p>
                  )}
                </div>
                {proposal.approver_comments && (
                  <div className="mt-4 p-3 bg-muted rounded">
                    <p className="text-sm font-medium mb-1">Approver Comments</p>
                    <p className="text-sm text-muted-foreground">{proposal.approver_comments}</p>
                  </div>
                )}
              </Card>

              {/* Documents Section */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold">Documents</h3>
                  {['admin', 'manager'].includes(user?.role) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.pdf,.doc,.docx';
                        input.onchange = (e) => {
                          if (e.target.files[0]) handleUploadDocument(e.target.files[0]);
                        };
                        input.click();
                      }}
                      data-testid="upload-doc-btn"
                    >
                      <Upload className="mr-1 h-3 w-3" />
                      Upload
                    </Button>
                  )}
                </div>
                {proposal.documents && proposal.documents.length > 0 ? (
                  <div className="space-y-2">
                    {proposal.documents.map((doc, idx) => (
                      <div key={doc.file_id || `detail-doc-${idx}`} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="truncate flex-1">{doc.original_filename || doc.filename}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(doc.uploaded_at)}</span>
                        {doc.view_link && (
                          <a href={doc.view_link} target="_blank" rel="noopener noreferrer" title="View" data-testid={`detail-view-doc-${idx}`}>
                            <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </a>
                        )}
                        {['admin', 'manager'].includes(user?.role) && (
                          <button onClick={() => handleDeleteDocument(idx)} title="Delete" data-testid={`detail-delete-doc-${idx}`} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-heading font-semibold mb-4">Actions</h3>
                <div className="space-y-2">
                  {/* Edit button - always available for creators */}
                  {isCreator && (
                    <Button className="w-full" onClick={() => setIsEditing(true)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Proposal
                    </Button>
                  )}
                  
                  {/* Draft status actions */}
                  {proposal.status === 'draft' && isCreator && (
                    <Button className="w-full" variant="outline" onClick={() => setIsApproverDialog(true)}>
                      <Send className="mr-2 h-4 w-4" />
                      Send for Approval
                    </Button>
                  )}
                  
                  {/* Pending approval - approver actions */}
                  {canApprove && (
                    <>
                      <Button className="w-full bg-green-500 hover:bg-green-600" onClick={handleApprove}>
                        <Check className="mr-2 h-4 w-4" />
                        Approve
                      </Button>
                      <Button className="w-full" variant="outline" onClick={() => setApproverComments('')}>
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Return with Comments
                      </Button>
                      <Button className="w-full" variant="destructive" onClick={handleReject}>
                        <X className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </>
                  )}
                  
                  {/* Sent to client - mark as signed or rejected */}
                  {proposal.status === 'sent_to_client' && (
                    <>
                      <Button className="w-full bg-green-500 hover:bg-green-600" onClick={() => handleStatusChange('signed')}>
                        <Check className="mr-2 h-4 w-4" />
                        Mark as Signed
                      </Button>
                      <Button className="w-full" variant="outline" onClick={() => handleStatusChange('rejected')}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Mark as Rejected
                      </Button>
                    </>
                  )}
                  
                  {/* Signed status - convert to project */}
                  {['signed', 'sent_to_client'].includes(proposal.status) && !proposal.project_id && (
                    <Button className="w-full bg-primary" onClick={handleConvert}>
                      <FileText className="mr-2 h-4 w-4" />
                      Convert to Project
                    </Button>
                  )}
                  
                  {/* Delete button */}
                  {isCreator && !proposal.project_id && (
                    <Button className="w-full" variant="destructive" onClick={handleDelete}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Proposal
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Extracted Dialogs */}
      <ApproverDialog
        open={isApproverDialog} onOpenChange={setIsApproverDialog}
        selectedApprover={selectedApprover} setSelectedApprover={setSelectedApprover}
        teamMembers={teamMembers} onSend={handleSendForInternalApproval}
      />
      <SendToClientDialog
        open={isSendDialog} onOpenChange={setIsSendDialog}
        recipientEmail={recipientEmail} setRecipientEmail={setRecipientEmail}
        onSend={handleSendToClient}
      />
      <ManualApprovalDialog
        open={isManualApprovalDialog} onOpenChange={setIsManualApprovalDialog}
        date={manualApprovalDate} setDate={setManualApprovalDate}
        onApprove={handleManualApproval}
      />
    </div>
  );
};
